'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { useEffect, useRef, useState } from 'react';

import { motion, useAnimation } from 'framer-motion';

import { MdOutlineStarPurple500 } from 'react-icons/md';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function CustomersReviews({ reviews }: { reviews: Reference[] }) {
  const [carouselWidth, setCarouselWidth] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  const duplicatedReviews = [...reviews, ...reviews];

  useEffect(() => {
    const updateWidth = () => {
      if (carouselRef.current) {
        const width = carouselRef.current.scrollWidth / 2;
        setCarouselWidth(width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    controls.start({
      x: -carouselWidth,
      transition: {
        duration: 140,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });

    return () => window.removeEventListener('resize', updateWidth);
  }, [carouselWidth, controls]);

  const handleAnimationComplete = () => {
    controls.set({ x: 0 });
    controls.start({
      x: -carouselWidth,
      transition: {
        duration: 140,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  };

  return (
    <section className="flex w-full flex-col items-center justify-center gap-7 bg-[#FAFAFA] py-16 sm:gap-10 sm:py-20 lg:gap-12 lg:py-24">
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <h3 className="text-center text-xl font-medium tracking-tighter sm:text-2xl lg:text-3xl">
          Spokojne u nás nakúpili
        </h3>
        <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <Image src={'/images/google-icon.svg'} alt="Google" width={24} height={24} />
            <div className="h-5 w-[1px] bg-black/20" />
            <div className="flex items-center justify-start gap-0.5 sm:gap-1">
              {[...Array(5)].map((_, index) => (
                <MdOutlineStarPurple500 size={20} className="text-primary" key={index} />
              ))}
            </div>
          </div>
          <p className="text-xs text-black/70 sm:text-[13px]">130+ Spokojných zákazníkov</p>
        </div>
      </div>
      <div className="relative z-10 w-full">
        <motion.div
          ref={carouselRef}
          className="flex flex-nowrap"
          animate={controls}
          onAnimationComplete={handleAnimationComplete}
        >
          {duplicatedReviews.map((review, index) => (
            <motion.div
              key={`${review.id}-${index}`}
              className="mx-4 flex-shrink-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <ReviewCard review={review} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: Reference }) {
  return (
    <motion.div
      className={cn(
        'relative flex w-[300px] flex-col items-start justify-start gap-4 bg-white p-4 sm:w-[320px] sm:gap-5 sm:p-5 md:w-[360px] md:p-6 lg:w-[381px] lg:gap-6 lg:p-6',
      )}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center justify-start gap-2">
          <div className="flex items-center justify-start gap-1">
            {[...Array(5)].map((_, i) => (
              <MdOutlineStarPurple500 key={i} size={20} className="text-primary" />
            ))}
          </div>
          <p className="text-sm font-semibold text-black lg:text-base">5.0</p>
        </div>
        <Image
          src={'/images/google-icon.svg'}
          alt="Google"
          width={0}
          height={0}
          className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <HtmlRenderer
          html={review.description}
          className={cn(
            'line-clamp-4 h-[64px] w-full text-black lg:line-clamp-5 lg:h-[100px]',
            'ea-landing-page__review-card-description',
          )}
        />
        <p className="text-sm font-semibold lg:text-base">{review.name}</p>
      </div>
    </motion.div>
  );
}
